import { Component } from '@theme/component';
import {
  fetchConfig,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #pendingQuantityChanges = new Map();
  #debouncedOnChange = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const line = event.detail?.cartLine;
    if (!line) return;
    const key = this.#getCartLineRow(line)?.dataset.key;
    if (!key) return;
    const previous = this.#pendingQuantityChanges.get(key);
    if (previous) {
      clearTimeout(previous.timer);
      previous.finish?.();
    }
    const finish = window.HalfpipedCartMutations?.begin();
    const timer = setTimeout(() => {
      this.#pendingQuantityChanges.delete(key);
      try {
        // Another completed update may replace rows or change line indexes
        // during this debounce. Keep the customer's intended line key.
        const currentRow = this.refs.cartItemRows.find(row => row.dataset.key === key);
        if (!currentRow) throw new Error('The cart item changed before its quantity update. Please review your cart.');
        const currentEvent = { target: currentRow, detail: { ...event.detail, cartLine: Number(currentRow.dataset.cartLineIndex) } };
        // A removal may open a confirmation and wait for idle. Its own
        // quantity debounce must finish before entering that workflow.
        if (event.detail.quantity === 0) finish?.();
        Promise.resolve(this.#onQuantityChange(currentEvent, finish)).then(() => finish?.(), error => finish?.(error));
      } catch (error) { finish?.(error); }
    }, 300);
    this.#pendingQuantityChanges.set(key, { timer, finish, event, bundleId: this.#getCartLineRow(line)?.dataset.boardBundleId });
  };
  #onBoardRemovalConfirm = event => {
    const paused = [];
    for (const [key, pending] of this.#pendingQuantityChanges) {
      if (pending.bundleId !== event.detail?.bundleId) continue;
      clearTimeout(pending.timer);
      pending.finish?.();
      this.#pendingQuantityChanges.delete(key);
      paused.push({ key, event: pending.event });
    }
    event.detail?.resume?.push(() => {
      const component = [...document.querySelectorAll('cart-items-component')]
        .find(candidate => candidate instanceof CartItemsComponent && candidate.sectionId === this.sectionId) || this;
      for (const pending of paused) {
        const row = component.refs.cartItemRows.find(item => item.dataset.key === pending.key);
        if (row) component.#debouncedOnChange({ target: row, detail: { ...pending.event.detail, cartLine: Number(row.dataset.cartLineIndex) } });
      }
    });
  };
  #cartChangeQueue = Promise.resolve();
  #pendingOptimisticRemovalQuantities = new Map();
  #pendingOptimisticRemovalTotals = new Map();
  #cartPageScrollIndicatorCleanup = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('hp:board-removal:confirming', this.#onBoardRemovalConfirm);

    this.#setupCartPageScrollIndicator();
    this.#primeCartItemImages();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('hp:board-removal:confirming', this.#onBoardRemovalConfirm);
    for (const { timer, finish } of this.#pendingQuantityChanges.values()) {
      clearTimeout(timer);
      finish?.(new Error('The cart changed before the quantity update finished. Please review your cart.'));
    }
    this.#pendingQuantityChanges.clear();
    this.#cartPageScrollIndicatorCleanup?.();
    this.#cartPageScrollIndicatorCleanup = null;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   * @param {() => void} [releaseDebounce] - Releases the queued intent after its write owns the barrier.
   */
  #onQuantityChange(event, releaseDebounce) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const lineItemRow = this.#getCartLineRow(line);
    if (!lineItemRow) return;

    if (this.refs.cartItemRows.some(row => row.dataset.boardBundleId)) {
      return this.#changeBoardCartQuantity(lineItemRow, quantity, releaseDebounce);
    }

    const update = this.updateQuantity({
      line,
      id: lineItemRow.dataset.key,
      quantity,
      action: 'change',
      releaseDebounce,
    });

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
    return update;
  }

  async #changeBoardCartQuantity(row, quantity, releaseDebounce) {
    const line = Number(row.dataset.cartLineIndex);
    this.#disableCartItems();
    try {
      const helper = window.HalfpipedBoardCartQuantity;
      if (!helper) throw new Error('Please reload the page before updating board parts.');
      const input = row.querySelector('cart-quantity-selector-component')?.querySelector('input');
      const maximum = input?.getAttribute?.('max');
      const result = await helper.change({ key: row.dataset.key, quantity,
        expectedQuantity: input ? Number(input.defaultValue) : undefined,
        maxQuantity: maximum ? Number(maximum) : null,
        maxStockLimited: input?.dataset?.stockLimited === 'true', releaseDebounce,
        onCart: (cart, confirmation) => this.#renderConfirmedCart(cart, confirmation),
      });
      return result.cart;
    } catch (error) {
      if (!window.HalfpipedBoardCartQuantity?.notifyQuantityLimit?.(error)) this.#showCartUpdateError(error, line);
      const selector = this.#getCartLineRow(line)?.querySelector('cart-quantity-selector-component');
      const input = selector?.querySelector('input');
      if (input) input.value = input.defaultValue;
      selector?.syncQuantitySelect?.();
      return null;
    } finally {
      releaseDebounce?.();
      this.#enableCartItems();
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getCartLineRow(line);

    if (!cartItemRowToRemove) return;
    if (cartItemRowToRemove.dataset.boardBundleId) return this.#removeBoard(line, cartItemRowToRemove);

    // The later remove intent supersedes any scheduled quantity for this row.
    this.#cancelPendingQuantityChanges([cartItemRowToRemove.dataset.key]);

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    const visibleRows = this.refs.cartItemRows.filter(
      (row) => !row.hidden && row.style.display !== 'none' && !row.classList.contains('removing')
    );
    const removedItemCount = this.#getRowsItemCount(rowsToRemove);
    const removedTotal = this.#getRowsLineTotal(rowsToRemove);
    const previousTotal = this.#getCurrentCartTotal();
    const holdBoardPricing = Boolean(this.querySelector('[data-board-cart-active]'));
    const optimisticItemCount = Math.max(0, this.#getCurrentCartItemCount() - removedItemCount);
    // Removing a required part can change the price of every remaining board
    // line. Keep the displayed price until the returned Liquid recalculates it.
    const optimisticTotal = holdBoardPricing ? previousTotal : Math.max(0, previousTotal - removedTotal);
    const optimisticKeys = rowsToRemove.map((row) => row.dataset.key).filter(Boolean);
    const lineItemKey = cartItemRowToRemove.dataset.key;

    for (const row of rowsToRemove) {
      if (row.dataset.key) {
        this.#pendingOptimisticRemovalQuantities.set(row.dataset.key, this.#getRowItemCount(row));
        this.#pendingOptimisticRemovalTotals.set(row.dataset.key, this.#getRowLineTotal(row));
      }
    }

    // If the cart item row is the last visible row, optimistically trigger the cart empty state.
    const isEmptyCart = rowsToRemove.length == visibleRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      this.#syncVisibleCartCount(optimisticItemCount);
      this.#syncVisibleCartTotal(optimisticTotal);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return this.updateQuantity({
        line,
        id: lineItemKey,
        quantity: 0,
        action: 'clear',
        optimisticRows: rowsToRemove,
        optimisticKeys,
        optimisticItemCountDelta: removedItemCount,
        previousItemCount: optimisticItemCount + removedItemCount,
        optimisticTotalDelta: removedTotal,
        previousTotal,
        optimisticEmptyCart: true,
        holdBoardPricing,
      });
    }

    // Match the cart drawer: hide immediately, then let Shopify confirm in the background.
    rowsToRemove.forEach((row) => {
      row.hidden = true;
      row.style.display = 'none';
      row.setAttribute('aria-hidden', 'true');
      row.classList.add('removing');
    });
    this.#syncVisibleCartCount(optimisticItemCount);
    this.#syncVisibleCartTotal(optimisticTotal);

    return this.updateQuantity({
      line,
      id: lineItemKey,
      quantity: 0,
      action: 'clear',
      optimisticRows: rowsToRemove,
      optimisticKeys,
      optimisticItemCountDelta: removedItemCount,
      optimisticTotalDelta: removedTotal,
      holdBoardPricing,
    });
  }

  #cancelPendingQuantityChanges(keys, bundleId) {
    for (const [key, pending] of this.#pendingQuantityChanges) {
      if (!keys.includes(key) && (!bundleId || pending.bundleId !== bundleId)) continue;
      clearTimeout(pending.timer);
      pending.finish?.();
      this.#pendingQuantityChanges.delete(key);
    }
  }

  async #removeBoard(line, row) {
    try {
      if (!window.HalfpipedBoardCartRemoval?.remove) throw new Error('Please reload the page before removing this board.');
      const sections = [...document.querySelectorAll('cart-items-component')].map(item => item.dataset.sectionId).filter(Boolean);
      sections.unshift(this.sectionId);
      const result = await window.HalfpipedBoardCartRemoval.remove({
        key: row.dataset.key, bundleId: row.dataset.boardBundleId, sections,
        cancelPending: ({ keys, bundleId }) => this.#cancelPendingQuantityChanges(keys, bundleId),
        onPending: () => { this.#disableCartItems(); this.setAttribute('aria-busy', 'true'); },
        onCart: (cart, confirmation) => this.#renderConfirmedCart(cart, confirmation),
      });
      if (result.cancelled) {
        const input = row.querySelector('cart-quantity-selector-component')?.querySelector('input');
        if (input?.value === '0') input.value = input.defaultValue;
      }
      return result.cart;
    } catch (error) {
      this.#showCartUpdateError(error, line);
      return null;
    } finally {
      this.#enableCartItems();
      this.removeAttribute('aria-busy');
    }
  }

  #showCartUpdateError(error, line) {
    const message = error?.message || 'Could not update your cart. Please try again.';
    const lineError = this.refs[`cartItemError-${line}`];
    const lineContainer = this.refs[`cartItemErrorContainer-${line}`];
    if (lineError && lineContainer) {
      lineError.textContent = message;
      lineContainer.classList.remove('hidden');
      return;
    }
    let notice = this.querySelector('[data-cart-update-error]');
    if (!notice) {
      notice = document.createElement('p');
      notice.setAttribute('data-cart-update-error', '');
      notice.setAttribute('role', 'alert');
      this.prepend(notice);
    }
    notice.textContent = message;
  }

  /**
   * Find Shopify's canonical cart line independently of its displayed position.
   * @param {number} line - The one-based Shopify cart line.
   * @returns {HTMLTableRowElement | undefined}
   */
  #getCartLineRow(line) {
    return this.refs.cartItemRows.find((row) => row.dataset.cartLineIndex === String(line));
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.id] - The line item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {HTMLTableRowElement[]} [config.optimisticRows] - Rows hidden before the request completes.
   * @param {string[]} [config.optimisticKeys] - Line item keys hidden before the request completes.
   * @param {number} [config.optimisticItemCountDelta] - Quantity hidden before the request completes.
   * @param {number} [config.optimisticTotalDelta] - Line total hidden before the request completes.
   * @param {number} [config.previousItemCount] - Item count before an optimistic empty-cart update.
   * @param {number} [config.previousTotal] - Cart total before an optimistic empty-cart update.
   * @param {boolean} [config.optimisticEmptyCart] - Whether the empty-cart view was already shown.
   * @param {boolean} [config.holdBoardPricing] - Await Shopify's complete-board recalculation.
   * @param {() => void} [config.releaseDebounce] - Releases this update's original quantity debounce.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const isOptimisticRemoval = config.action === 'clear' && Array.isArray(config.optimisticRows);

    if (!isOptimisticRemoval) {
      this.#disableCartItems();
    }

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const bodyPayload = {
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    if (config.id) {
      bodyPayload.id = config.id;
    } else {
      bodyPayload.line = line;
    }

    const body = JSON.stringify(bodyPayload);

    if (!isOptimisticRemoval || config.holdBoardPricing) {
      cartTotal?.shimmer();
    }

    const finishCartMutation = window.HalfpipedCartMutations?.begin();
    // Transfer this intent's barrier synchronously, before the queued write can
    // start. Other pending writers still prevent a confirmed snapshot.
    config.releaseDebounce?.();
    let confirmation;
    const runUpdate = () => {
      const mutations = window.HalfpipedCartMutations;
      const exclusive = mutations?.getPendingCount?.() === 1;
      const request = fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      confirmation = { confirmed: exclusive, cartRevision: mutations?.getRevision() };
      return request
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          throw new Error(String(parsedResponseText.description || parsedResponseText.errors));
        }

        this.#confirmOptimisticRemoval(config);
        confirmation.confirmed = confirmation.confirmed && confirmation.cartRevision === window.HalfpipedCartMutations?.getRevision();
        await this.#renderConfirmedCart(parsedResponseText, confirmation);
        return parsedResponseText;
      })
      .catch((error) => {
        finishCartMutation?.(error);
        this.#restoreOptimisticRemoval(config);
        this.#showCartUpdateError(error, line);
        if (!isOptimisticRemoval) {
          const selector = this.#getCartLineRow(line)?.querySelector('cart-quantity-selector-component');
          const input = selector?.querySelector('input');
          if (input) input.value = input.defaultValue;
          selector?.syncQuantitySelect?.();
        }
        return null;
      })
      .finally(() => {
        finishCartMutation?.();
        if (!isOptimisticRemoval) {
          this.#enableCartItems();
        }
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
    };

    const queuedUpdate = this.#cartChangeQueue.then(runUpdate, runUpdate);
    this.#cartChangeQueue = queuedUpdate.catch(() => {});
    return queuedUpdate;
  }

  async #renderConfirmedCart(cart, confirmation = {}) {
    const sectionHTML = cart.sections?.[this.sectionId];
    let sections = cart.sections;
    let itemCount = cart.item_count;
    let html;
    if (typeof sectionHTML === 'string' && sectionHTML.trim()) {
      const prepared = this.#prepareSectionHTMLForPendingRemovals(sectionHTML);
      const parsed = new DOMParser().parseFromString(prepared.html, 'text/html');
      const count = parsed.querySelector('[ref="cartItemCount"]')?.textContent;
      itemCount = count ? Number.parseInt(count, 10) : prepared.itemCount;
      sections = { ...cart.sections, [this.sectionId]: prepared.html };
      html = prepared.html;
      this.#syncVisibleCartCount(itemCount);
      this.#syncVisibleCartTotal(prepared.total);
    }
    this.#updateQuantitySelectors(cart);
    this.dispatchEvent(new CartUpdateEvent(cart, this.sectionId, {
      itemCount, source: 'cart-items-component', sections,
      cartConfirmed: confirmation.confirmed === true, cartRevision: confirmation.cartRevision,
    }));
    if (html) morphSection(this.sectionId, html, { mode: this.isDrawer ? 'hydration' : 'full' });
    else {
      // Shopify can succeed at the cart update but fail to render a section.
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
    this.#updateCartQuantitySelectorButtonStates();
    if (confirmation.confirmed) {
      window.HalfpipedCartMutations?.acknowledgeCart?.(confirmation.cartRevision);
      window.HalfpipedBoardCartRemoval?.rememberCart?.(cart, confirmation);
    }
    window.requestAnimationFrame(() => {
      this.#setupCartPageScrollIndicator();
      this.#primeCartItemImages();
    });
  }

  /**
   * Restores rows hidden for an optimistic removal when Shopify rejects the update.
   * @param {Object} config - The update config.
   * @param {HTMLTableRowElement[]} [config.optimisticRows] - Rows hidden before the request completes.
   * @param {boolean} [config.optimisticEmptyCart] - Whether the empty-cart view was already shown.
   */
  #restoreOptimisticRemoval(config) {
    this.#clearPendingOptimisticRemoval(config);

    if (config.optimisticEmptyCart) {
      if (typeof config.previousItemCount === 'number') {
        this.#syncVisibleCartCount(config.previousItemCount);
      }
      if (typeof config.previousTotal === 'number') {
        this.#syncVisibleCartTotal(config.previousTotal);
      }
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (!Array.isArray(config.optimisticRows)) return;

    if (config.optimisticItemCountDelta) {
      this.#syncVisibleCartCount(this.#getCurrentCartItemCount() + config.optimisticItemCountDelta);
    }

    if (config.optimisticTotalDelta && !config.holdBoardPricing) {
      this.#syncVisibleCartTotal(this.#getCurrentCartTotal() + config.optimisticTotalDelta);
    }

    for (const row of config.optimisticRows) {
      row.classList.remove('removing');
      row.hidden = false;
      row.style.removeProperty('display');
      row.removeAttribute('aria-hidden');
      row.style.removeProperty('--row-height');
    }
  }

  /**
   * Removes confirmed keys from the pending optimistic-removal map.
   * @param {Object} config - The update config.
   * @param {string[]} [config.optimisticKeys] - Line item keys hidden before the request completes.
   */
  #confirmOptimisticRemoval(config) {
    this.#clearPendingOptimisticRemoval(config);
  }

  /**
   * Clears pending optimistic-removal keys for a completed or rejected update.
   * @param {Object} config - The update config.
   * @param {string[]} [config.optimisticKeys] - Line item keys hidden before the request completes.
   */
  #clearPendingOptimisticRemoval(config) {
    if (!Array.isArray(config.optimisticKeys)) return;

    for (const key of config.optimisticKeys) {
      this.#pendingOptimisticRemovalQuantities.delete(key);
      this.#pendingOptimisticRemovalTotals.delete(key);
    }
  }

  /**
   * Keeps still-pending removals hidden when an earlier cart response morphs the section.
   * @param {string} sectionHTML - Cart section HTML from Shopify.
   * @returns {{ html: string, itemCount: number, total: number }}
   */
  #prepareSectionHTMLForPendingRemovals(sectionHTML) {
    const sectionDocument = new DOMParser().parseFromString(sectionHTML, 'text/html');
    let itemCount = parseInt(sectionDocument.querySelector('[ref="cartItemCount"]')?.textContent || '0', 10) || 0;
    let total = this.#getSectionDocumentCartTotal(sectionDocument);
    const holdBoardPricing = Boolean(sectionDocument.querySelector('[data-board-cart-active]'));

    if (this.#pendingOptimisticRemovalQuantities.size > 0) {
      const pendingRows = sectionDocument.querySelectorAll('[data-key]');

      for (const row of pendingRows) {
        if (!(row instanceof HTMLElement) || !row.dataset.key) continue;
        if (!this.#pendingOptimisticRemovalQuantities.has(row.dataset.key)) continue;

        itemCount = Math.max(
          0,
          itemCount - (this.#getRowItemCount(row) || this.#pendingOptimisticRemovalQuantities.get(row.dataset.key) || 0)
        );
        if (!holdBoardPricing) {
          total = Math.max(
            0,
            total - (this.#getRowLineTotal(row) || this.#pendingOptimisticRemovalTotals.get(row.dataset.key) || 0)
          );
        }
        row.remove();
      }
    }

    this.#syncSectionDocumentCartCount(sectionDocument, itemCount);
    this.#syncSectionDocumentCartTotal(sectionDocument, total);

    return {
      html: sectionDocument.body.innerHTML,
      itemCount,
      total,
    };
  }

  /**
   * Gets the total item quantity represented by rows.
   * @param {HTMLTableRowElement[]} rows - Cart rows.
   * @returns {number}
   */
  #getRowsItemCount(rows) {
    return rows.reduce((sum, row) => sum + this.#getRowItemCount(row), 0);
  }

  /**
   * Gets the item quantity represented by a row.
   * @param {HTMLElement} row - Cart row.
   * @returns {number}
   */
  #getRowItemCount(row) {
    const quantityInput = row.querySelector('input[data-cart-line], input[name="updates[]"]');
    const value = Number(quantityInput?.value);

    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /**
   * Gets the total line price represented by rows.
   * @param {HTMLTableRowElement[]} rows - Cart rows.
   * @returns {number}
   */
  #getRowsLineTotal(rows) {
    return rows.reduce((sum, row) => sum + this.#getRowLineTotal(row), 0);
  }

  /**
   * Gets the displayed line price represented by a row.
   * @param {HTMLElement} row - Cart row.
   * @returns {number}
   */
  #getRowLineTotal(row) {
    const priceNode = row.querySelector('.cart-items__price text-component, .cart-items__price');
    const priceText = priceNode?.getAttribute('value') || priceNode?.textContent || '';

    return this.#parseMoneyText(priceText);
  }

  /**
   * Reads the currently displayed cart item count.
   * @returns {number}
   */
  #getCurrentCartItemCount() {
    const countSources = [
      this.querySelector('[ref="cartItemCount"]')?.textContent,
      document.querySelector('.hp-cart-page .cart-title__count')?.textContent,
      document.querySelector('[data-cart-label]')?.textContent,
      document.querySelector('[data-cart-count-badge]:not(.is-hidden)')?.textContent,
    ];

    for (const value of countSources) {
      const count = Number.parseInt(value?.match(/\d+/)?.[0] || '', 10);
      if (Number.isFinite(count)) return count;
    }

    return 0;
  }

  /**
   * Reads the currently displayed cart total.
   * @returns {number}
   */
  #getCurrentCartTotal() {
    const totalNode = document.querySelector('.hp-cart-page [data-cart-subtotal], .hp-cart-page .cart-totals__total-value');
    const totalText = totalNode?.getAttribute('value') || totalNode?.textContent || '';

    return this.#parseMoneyText(totalText);
  }

  /**
   * Reads cart total from a parsed cart section.
   * @param {Document} sectionDocument - Parsed cart section document.
   * @returns {number}
   */
  #getSectionDocumentCartTotal(sectionDocument) {
    const totalNode = sectionDocument.querySelector('[data-cart-subtotal], .cart-totals__total-value');
    const totalText = totalNode?.getAttribute('value') || totalNode?.textContent || '';

    return this.#parseMoneyText(totalText);
  }

  /**
   * Parses a displayed money string into a number.
   * @param {string} value - Money text.
   * @returns {number}
   */
  #parseMoneyText(value) {
    const match = String(value).replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
    const amount = match ? Number(match[0]) : 0;

    return Number.isFinite(amount) ? amount : 0;
  }

  /**
   * Formats a number using the existing money text as the template.
   * @param {number} amount - Amount to format.
   * @param {string} template - Existing money text.
   * @returns {string}
   */
  #formatMoneyAmount(amount, template) {
    const safeAmount = Math.max(0, Number(amount) || 0);
    const templateText = String(template || '');
    const match = templateText.match(/-?\d[\d,]*(?:\.\d+)?/);
    const numericTemplate = match?.[0] || '0.00';
    const decimalCount = numericTemplate.includes('.') ? numericTemplate.split('.')[1].length : 0;
    const fixedAmount = safeAmount.toFixed(decimalCount);
    const [integerPart, decimalPart] = fixedAmount.split('.');
    const formattedInteger = numericTemplate.includes(',')
      ? integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      : integerPart;
    const formattedAmount = decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;

    if (!match || match.index === undefined) return formattedAmount;

    return `${templateText.slice(0, match.index)}${formattedAmount}${templateText.slice(match.index + match[0].length)}`;
  }

  /**
   * Syncs visible cart count UI immediately.
   * @param {number} itemCount - Cart item count.
   */
  #syncVisibleCartCount(itemCount) {
    const count = Math.max(0, Number(itemCount) || 0);
    const badgeText = count > 99 ? '99+' : String(count);
    const label = count === 1 ? 'item' : 'items';

    this.querySelectorAll('[ref="cartItemCount"]').forEach((node) => {
      node.textContent = String(count);
    });

    document.querySelectorAll('.hp-cart-page .cart-title__count').forEach((node) => {
      node.textContent = `(${count})`;
    });

    document.querySelectorAll('[data-cart-label]').forEach((node) => {
      node.textContent = `Cart (${count})`;
    });

    document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-label', `Open cart, ${count} ${label}`);
    });

    document.querySelectorAll('[data-cart-count-badge]').forEach((badge) => {
      badge.textContent = badgeText;
      badge.classList.toggle('is-wide', badgeText.length > 1);
      badge.classList.toggle('is-hidden', count <= 0);
      badge.hidden = count <= 0;

      if (count > 0) {
        badge.style.display = 'inline-flex';
        badge.style.visibility = 'visible';
        badge.style.opacity = '1';
      } else {
        badge.style.removeProperty('display');
        badge.style.removeProperty('visibility');
        badge.style.removeProperty('opacity');
      }
    });

    document.querySelectorAll('cart-icon').forEach((cartIcon) => {
      cartIcon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
      cartIcon.querySelectorAll('.cart-bubble').forEach((bubble) => {
        bubble.classList.toggle('visually-hidden', count <= 0);
      });
      cartIcon.querySelectorAll('.cart-bubble__text-count').forEach((countNode) => {
        countNode.textContent = count < 100 ? String(count) : '';
        countNode.classList.toggle('hidden', count <= 0);
      });
    });

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(count),
        timestamp: Date.now(),
      })
    );
  }

  /**
   * Syncs visible cart total UI immediately.
   * @param {number} total - Cart total.
   */
  #syncVisibleCartTotal(total) {
    document.querySelectorAll('.hp-cart-page [data-cart-subtotal], .hp-cart-page .cart-totals__total-value').forEach((node) => {
      const template = node.getAttribute('value') || node.textContent || '';
      const formattedTotal = this.#formatMoneyAmount(total, template);

      node.textContent = formattedTotal;
      node.setAttribute('value', formattedTotal);
    });
  }

  /**
   * Syncs total text inside a parsed section before morphing.
   * @param {Document} sectionDocument - Parsed cart section document.
   * @param {number} itemCount - Cart item count.
   */
  #syncSectionDocumentCartCount(sectionDocument, itemCount) {
    const count = Math.max(0, Number(itemCount) || 0);

    sectionDocument.querySelectorAll('[ref="cartItemCount"]').forEach((node) => {
      node.textContent = String(count);
    });

    sectionDocument.querySelectorAll('.cart-title__count').forEach((node) => {
      node.textContent = `(${count})`;
    });
  }

  /**
   * Syncs count text inside a parsed section before morphing.
   * @param {Document} sectionDocument - Parsed cart section document.
   * @param {number} total - Cart total.
   */
  #syncSectionDocumentCartTotal(sectionDocument, total) {
    sectionDocument.querySelectorAll('[data-cart-subtotal], .cart-totals__total-value').forEach((node) => {
      const template = node.getAttribute('value') || node.textContent || '';
      const formattedTotal = this.#formatMoneyAmount(total, template);

      node.textContent = formattedTotal;
      node.setAttribute('value', formattedTotal);
    });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#getCartLineRow(line)?.querySelector('cart-quantity-selector-component');
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;
    if ('syncQuantitySelect' in quantitySelector && typeof quantitySelector.syncQuantitySelect === 'function') {
      quantitySelector.syncQuantitySelect();
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      window.requestAnimationFrame(() => {
        this.#setupCartPageScrollIndicator();
        this.#primeCartItemImages();
      });
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!Array.isArray(updatedCart.items)) return;

    const quantitiesByVariant = new Map();
    for (const item of updatedCart.items) {
      if (item.variant_id == null) continue;
      const variantId = item.variant_id.toString();
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + (Number(item.quantity) || 0));
    }

    const selectors = document.querySelectorAll('quantity-selector-component[data-variant-id], cart-quantity-selector-component[data-variant-id]');

    for (const selector of selectors) {
      const variantId = selector.getAttribute('data-variant-id');
      const input = selector.querySelector('input[data-cart-quantity]');
      if (!variantId || !input) continue;

      input.setAttribute('data-cart-quantity', (quantitiesByVariant.get(variantId) || 0).toString());

      if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
        selector.updateCartQuantity();
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  #primeCartItemImages() {
    const images = this.querySelectorAll('.hp-cart-page .cart-items__media-image');

    images.forEach((image, index) => {
      if (!(image instanceof HTMLImageElement)) return;

      image.loading = 'eager';
      image.decoding = 'async';
      image.setAttribute('fetchpriority', index === 0 ? 'high' : 'auto');
      if (image.dataset.cartImagePrimed === 'true') return;
      image.dataset.cartImagePrimed = 'true';

      const retryBrokenImage = () => {
        if (!image.complete || image.naturalWidth !== 0) return;

        const src = image.currentSrc || image.src;
        if (!src) return;

        image.src = '';
        window.requestAnimationFrame(() => {
          image.src = src;
        });
      };

      image.addEventListener('error', retryBrokenImage, { once: true });
      window.setTimeout(retryBrokenImage, 800);
    });
  }

  #setupCartPageScrollIndicator() {
    this.#cartPageScrollIndicatorCleanup?.();
    this.#cartPageScrollIndicatorCleanup = null;

    const cartBody = this.querySelector('.hp-cart-page:not(.cart-page--empty) .cart-page__body');
    const cartItems = this.querySelector('.hp-cart-page:not(.cart-page--empty) .cart-page__items');
    const cartSummary = this.querySelector('.hp-cart-page:not(.cart-page--empty) .cart-page__summary');

    if (!(cartBody instanceof HTMLElement) || !(cartItems instanceof HTMLElement)) return;

    const indicator = document.createElement('div');
    indicator.className = 'hp-cart-scroll-indicator';
    indicator.setAttribute('aria-hidden', 'true');
    indicator.innerHTML = '<span class="hp-cart-scroll-indicator__thumb"></span>';
    cartBody.append(indicator);

    let frameId = 0;
    const update = () => {
      if (cartSummary instanceof HTMLElement) {
        const summaryHeight = Math.ceil(cartSummary.getBoundingClientRect().height);
        cartBody.style.setProperty('--hp-cart-summary-height', `${summaryHeight}px`);
      }

      const maxScroll = Math.max(0, cartItems.scrollHeight - cartItems.clientHeight);
      const canScroll = maxScroll > 1;
      cartBody.classList.toggle('hp-cart-page__body--has-scroll', canScroll);
      indicator.hidden = !canScroll;

      if (!canScroll) return;

      const bodyRect = cartBody.getBoundingClientRect();
      const itemsRect = cartItems.getBoundingClientRect();
      const trackEndInset = 1;
      const trackTop = Math.max(0, Math.round(itemsRect.top - bodyRect.top));
      const trackHeight = Math.max(34, Math.floor(itemsRect.bottom - bodyRect.top - trackTop - trackEndInset));
      const thumbHeight = Math.max(34, Math.round((trackHeight * cartItems.clientHeight) / cartItems.scrollHeight));
      const thumbTravel = Math.max(0, trackHeight - thumbHeight);
      const thumbY = Math.round(thumbTravel * (cartItems.scrollTop / maxScroll));

      indicator.style.setProperty('--hp-cart-scroll-track-top', `${trackTop}px`);
      indicator.style.setProperty('--hp-cart-scroll-track-height', `${trackHeight}px`);
      indicator.style.setProperty('--hp-cart-scroll-thumb-height', `${thumbHeight}px`);
      indicator.style.setProperty('--hp-cart-scroll-thumb-y', `${thumbY}px`);
    };
    const scheduleUpdate = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        update();
      });
    };
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(scheduleUpdate) : null;

    cartItems.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    resizeObserver?.observe(cartItems);
    resizeObserver?.observe(cartBody);
    if (cartSummary instanceof HTMLElement) resizeObserver?.observe(cartSummary);
    scheduleUpdate();

    this.#cartPageScrollIndicatorCleanup = () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      cartItems.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      resizeObserver?.disconnect();
      cartBody.classList.remove('hp-cart-page__body--has-scroll');
      indicator.remove();
    };
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
